/**
 * URL Sanitization Utility
 * Prevents XSS attacks via malicious URLs and normalizes URL formats
 */

/**
 * Check if a URL uses a dangerous protocol
 */
export function isDangerousUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  
  const lower = url.toLowerCase().trim();
  
  // Block dangerous protocols
  const dangerousProtocols = [
    'javascript:',
    'data:',
    'vbscript:',
    'file:',
    'about:',
  ];
  
  return dangerousProtocols.some(protocol => lower.startsWith(protocol));
}

/**
 * Sanitize and normalize a URL
 * - Blocks dangerous protocols (javascript:, data:, etc.)
 * - Auto-prepends https:// for protocol-less URLs
 * - Trims whitespace
 * - Returns empty string for invalid URLs
 */
export function sanitizeUrl(url: string | undefined | null): string {
  // Handle empty/null/undefined
  if (!url || typeof url !== 'string') return '';
  
  const trimmed = url.trim();
  if (!trimmed) return '';
  
  // Block dangerous protocols
  if (isDangerousUrl(trimmed)) {
    console.warn(`Blocked dangerous URL: ${trimmed.substring(0, 50)}...`);
    return '';
  }
  
  const lower = trimmed.toLowerCase();
  
  // If URL already has a safe protocol, return as-is
  if (lower.startsWith('http://') || lower.startsWith('https://')) {
    return trimmed;
  }
  
  // Auto-prepend https:// for protocol-less URLs
  // This handles cases like "twitter.com/user" -> "https://twitter.com/user"
  return `https://${trimmed}`;
}

/**
 * Validate if a string is a valid URL
 * More strict than sanitizeUrl - returns false for invalid formats
 */
export function isValidUrl(url: string | undefined | null): boolean {
  if (!url || typeof url !== 'string') return false;
  
  try {
    // Try to construct URL object (will throw if invalid)
    const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
    
    // Must be http or https
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      return false;
    }
    
    // Must have a valid hostname
    if (!urlObj.hostname || urlObj.hostname.length === 0) {
      return false;
    }
    
    return true;
  } catch {
    return false;
  }
}

/**
 * Sanitize an object containing URL fields
 * Useful for social links objects
 */
export function sanitizeUrlObject<T extends Record<string, any>>(
  obj: T,
  urlFields: (keyof T)[]
): T {
  const sanitized = { ...obj };
  
  for (const field of urlFields) {
    if (typeof sanitized[field] === 'string') {
      sanitized[field] = sanitizeUrl(sanitized[field] as string) as T[keyof T];
    }
  }
  
  return sanitized;
}

/**
 * Sanitize an array of link objects (e.g., custom links)
 * Filters out links with invalid URLs
 */
export function sanitizeCustomLinks(
  links: Array<{ label: string; url: string }>
): Array<{ label: string; url: string }> {
  return links
    .map(link => ({
      label: link.label?.trim() || '',
      url: sanitizeUrl(link.url),
    }))
    .filter(link => link.label && link.url); // Remove invalid links
}

